import { PACKET_ENUM } from "enums";
import React, { useEffect, useRef, useState } from "react";
import "./ChoosePacketStep.scss";
import SinglePacket from "./SinglePacket";
import { Button, CouponPackagePro } from "shared";
import { ParentType } from "../../PacketModal";
import { useChangePacket } from "hooks/users";
import { useGetCustomer } from "hooks/customers";
import {
  useActivateSubscription,
  useGetSubscription,
} from "hooks/subscriptions";
import { useGetPaymentMethods } from "hooks/payments";
import { useGetMe } from "hooks/auth";
import { DiscountType } from "types/DiscountType";
import {
  getDiscountFromStorage,
  saveDiscountToStorage,
} from "utils/discountStorage";
import { isDesktop } from "utils";

interface Props {
  onClose: () => void;
  parent: ParentType;
  goNextStep: () => void;
  setStepOptions?: any;
  stepOptions?: any;
  setFormData?: ({ packet }: any) => void;
}

const ChoosePacketStep: React.FC<Props> = ({
  onClose,
  parent,
  goNextStep,
  setStepOptions,
  stepOptions,
  setFormData,
}) => {
  const [packet, setPacket] = useState<PACKET_ENUM | null>(
    stepOptions?.ChoosePacketStep?.data || null,
  );
  const [selectedPacket, setSelectedPacket] = useState<PACKET_ENUM | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountType | null>(
    null,
  );
  const packetsContainerRef = useRef<HTMLDivElement | null>(null);
  const { mutateAsync: activateSubscription } = useActivateSubscription();
  const { data: authorizedUser } = useGetMe();
  const { data: customerData } = useGetCustomer(!!authorizedUser);
  const { mutateAsync: changePacket } = useChangePacket();
  const { data: paymentMethods } = useGetPaymentMethods(!!authorizedUser);
  const { data: subscription } = useGetSubscription(!!authorizedUser);
  const card = paymentMethods?.find((paymentMethod) => paymentMethod.default);

  useEffect(() => {
    const savedDiscount = getDiscountFromStorage();
    if (savedDiscount) {
      setAppliedDiscount(savedDiscount);
    }
  }, []);

  const handleDiscountApplied = (discount: DiscountType) => {
    setAppliedDiscount(discount);
    saveDiscountToStorage(discount);

    if (!isDesktop()) {
      setTimeout(() => {
        const proPacketElement = packetsContainerRef.current?.querySelector(
          '[data-packet="pro"]',
        ) as HTMLElement | null;

        if (!proPacketElement) return;

        const offset = 70;
        const modalContent = document.querySelector(
          ".fullscreen-modal-overlay",
        ) as HTMLElement | null;

        if (modalContent && modalContent.contains(proPacketElement)) {
          const elementTopWithinContainer =
            proPacketElement.getBoundingClientRect().top -
            modalContent.getBoundingClientRect().top +
            modalContent.scrollTop;

          modalContent.scrollTo({
            top: Math.max(0, elementTopWithinContainer - offset),
            behavior: "smooth",
          });
          return;
        }

        const elementTop =
          proPacketElement.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: Math.max(0, elementTop - offset),
          behavior: "smooth",
        });
      }, 0);
    }
  };

  const handlePacketChange = async (packet: PACKET_ENUM) => {
    setIsSubmitting(true);
    try {
      if (setStepOptions)
        setStepOptions({ ...stepOptions, ChoosePacketStep: { data: packet } });
      sessionStorage.setItem("packet", packet);
      // setPacket(packet);

      if (parent === "homepage") {
        setFormData && setFormData({ packet });
        goNextStep();
      }

      if (parent === "payment") {
        if (!card) {
          sessionStorage.setItem("packet", packet);
          goNextStep();
          return;
        }

        const discountCode =
          appliedDiscount?.code || customerData?.discounts?.[0]?.code;

        if (
          (!customerData?.subscriptionUid || subscription?.canceled_at) &&
          card
        )
          await activateSubscription({
            payment_method_uid: card.id,
            selectedPackage: packet,
            ...(discountCode && { discountCode }),
          });
        await changePacket(
          { packet, discountCode },
          { onSuccess: () => onClose() },
        );
      }
    } finally {
      setIsSubmitting(false);
      // setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setPacket(authorizedUser?.package ?? null);
  }, [authorizedUser?.package]);

  const isFromPayment = parent === "payment";
  const isFromHomepage = parent === "homepage";

  const hasCustomerDiscount = !!customerData?.discounts?.length;

  return (
    <>
      <h2 className="fullscreen-modal__header packet-modal__header">
        Wybierz pakiet, który Cię interesuje
      </h2>
      <div className="choose-packet-step__wrapper">
        {!hasCustomerDiscount && (
          <CouponPackagePro onDiscountApplied={handleDiscountApplied} />
        )}

        <div className="choose-packet-step" ref={packetsContainerRef}>
          <SinglePacket
            packetName={PACKET_ENUM.BASIC}
            currentPacket={packet}
            selectedPacket={selectedPacket}
            setSelectedPacket={setSelectedPacket}
            parent={parent}
            discount={appliedDiscount}
            customerDiscounts={customerData?.discounts}
          />
          <SinglePacket
            packetName={PACKET_ENUM.STANDARD}
            currentPacket={packet}
            selectedPacket={selectedPacket}
            setSelectedPacket={setSelectedPacket}
            parent={parent}
            discount={appliedDiscount}
            customerDiscounts={customerData?.discounts}
          />
          <SinglePacket
            packetName={PACKET_ENUM.PREMIUM}
            currentPacket={packet}
            selectedPacket={selectedPacket}
            setSelectedPacket={setSelectedPacket}
            parent={parent}
            discount={appliedDiscount}
            customerDiscounts={customerData?.discounts}
          />
          <SinglePacket
            packetName={PACKET_ENUM.PRO}
            currentPacket={packet}
            selectedPacket={selectedPacket}
            setSelectedPacket={setSelectedPacket}
            parent={parent}
            discount={appliedDiscount}
            customerDiscounts={customerData?.discounts}
          />
        </div>
        <div
          className={`choose-packet-step__submit-buttons ${
            !!selectedPacket ? "choose-packet-step__submit-buttons--show" : ""
          }
          choose-packet-step__submit-buttons--right
          choose-packet-step__submit-buttons--${parent} 
          `}
        >
          {isFromPayment ? (
            <>
              <Button
                className="button--rounded button--black button--stroke choose-packet-step__button"
                label="Zapisz zmiany"
                onClick={() =>
                  selectedPacket && handlePacketChange(selectedPacket)
                }
                isLoading={isSubmitting}
              />
            </>
          ) : null}
          {isFromHomepage ? (
            <Button
              className="button--rounded button--black button--stroke choose-packet-step__button"
              label="Dalej"
              onClick={() =>
                selectedPacket && handlePacketChange(selectedPacket)
              }
              isLoading={isSubmitting}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

export default ChoosePacketStep;
